import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { RenderSidePhase, RenderSides, RenderVeil } from '../render-flow.model';
import { renderDimensions } from '../render.data';

type Orientation = 'landscape' | 'portrait';

// What a screen says while its orientation is on its way: the stages card's words (owner,
// 2026-10-20, decision 40). Waiting for its GPU job is setting up the studio, the job loading and
// opening the model is checking it, the frame and its saving are rendering.
const SETUP_LABEL = 'Setting up the scene…';
const CHECKING_LABEL = 'Checking the model…';
const PHASE_LABELS: Readonly<Record<RenderSidePhase, string>> = {
  waiting: SETUP_LABEL,
  loading: CHECKING_LABEL,
  rendering: 'Rendering…',
  saving: 'Rendering…',
  done: 'Rendering…',
};

// The card's stage for each phase, and the phase that says a stage: a screen never says more
// than the card has ticked (it draws its lines a moment behind the renderer, the bar first).
const STAGE_OF_PHASE: Readonly<Record<RenderSidePhase, number>> = { waiting: 0, loading: 1, rendering: 2, saving: 2, done: 2 };
const PHASE_OF_STAGE: ReadonlyArray<RenderSidePhase> = ['waiting', 'loading', 'rendering'];
const STAGE_OF_PENDING: Readonly<Record<string, number>> = { setup: 0, checking: 1, rendering: 2 };

// The preview frames on the designer's devices (Figma 28516:21496 / 28520:21850): the display
// and the phone side by side, bottoms level, each with a full-screen button and its size under
// it. Stills only — the free preview is a frame, the videos come after the purchase.
//
// Processing shows the same devices with nothing on them yet (`pending`): an empty dark studio
// and what is happening. No frame exists before the render ends, so none is shown (owner,
// 2026-09-23). The preview step then `reveal`s: each frame develops on its screen once loaded.
// A failed preview keeps the empty studio ('failed': "Couldn't render"); failed videos keep the
// preview's frames, dimmed, with where the render stopped (`stopped`).
//
// On the dev render backend each orientation is its own GPU job (`sides`): each screen says where
// its own one stands, and a frame that is done develops on its screen while the other renders
// (owner, 2026-10-01: the page goes by the renderer's real states).
@Component({
  selector: 'app-render-still-devices',
  templateUrl: './render-still-devices.component.html',
  styleUrls: ['./render-still-devices.component.scss'],
})
export class RenderStillDevicesComponent implements OnDestroy {
  @Input() landscape = '';
  @Input() portrait = '';
  // The model's name for the alt texts.
  @Input() name = '';
  @Input() pending: 'uploading' | 'setup' | 'checking' | 'rendering' | 'failed' | null = null;
  // Each orientation's own state while pending (the dev render backend only).
  @Input() sides: RenderSides | null | undefined = null;
  // Over dimmed frames: "Stopped at 62%".
  @Input() stopped: string | null = null;
  @Input() reveal = false;
  // A paid model's frames still with the preview's watermark: blurred until the clean ones come, full
  // screen too (renderFrameVeiled).
  @Input() veiled: RenderVeil | null = null;

  public landscapeLoaded = false;
  public portraitLoaded = false;
  // The frame open full screen, if any.
  public viewing: Orientation | null = null;

  @ViewChild('viewer') private viewer?: ElementRef<HTMLElement>;
  @ViewChild('viewerClose') private viewerClose?: ElementRef<HTMLButtonElement>;
  private returnFocus: HTMLElement | null = null;

  public readonly landscapeSize = renderDimensions('landscape');
  public readonly portraitSize = renderDimensions('portrait');

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnDestroy(): void {
    this.closeViewer();
  }

  public get stateLabel(): string {
    switch (this.pending) {
      case 'failed':
        return 'Couldn’t render';
      case 'uploading':
        return 'Uploading…';
      case 'checking':
        return CHECKING_LABEL;
      case 'rendering':
        return 'Rendering…';
      default:
        return SETUP_LABEL;
    }
  }

  // An orientation done while the other still renders: its frame, on its screen already.
  public earlyFrame(orientation: Orientation): string {
    if (!this.pending || this.pending === 'failed' || !this.sides) {
      return '';
    }
    return (orientation === 'landscape' ? this.sides.landscapeFrame : this.sides.portraitFrame) || '';
  }

  public isPending(orientation: Orientation): boolean {
    return !!this.pending && !this.earlyFrame(orientation);
  }

  public isVeiled(orientation: Orientation): boolean {
    return !!this.veiled?.[orientation];
  }

  public srcOf(orientation: Orientation): string {
    return this.earlyFrame(orientation) || (orientation === 'landscape' ? this.landscape : this.portrait);
  }

  public stateLabelOf(orientation: Orientation): string {
    const phase = this.phaseOf(orientation);
    return phase ? PHASE_LABELS[phase] : this.stateLabel;
  }

  public isRenderingOf(orientation: Orientation): boolean {
    const phase = this.phaseOf(orientation);
    return phase ? phase === 'rendering' || phase === 'saving' : this.pending === 'rendering';
  }

  // The orientation's own phase, up to the card's current stage (`pending`).
  private phaseOf(orientation: Orientation): RenderSidePhase | null {
    const cap = this.pending ? STAGE_OF_PENDING[this.pending] : undefined;
    if (!this.sides || cap === undefined) {
      return null;
    }
    const phase = this.sides[orientation];
    return STAGE_OF_PHASE[phase] > cap ? PHASE_OF_STAGE[cap] : phase;
  }

  // The viewer goes full screen with its close button; where elements cannot (iPhone Safari), it
  // stays what it already is, a fixed overlay over the page.
  public openFullscreen(orientation: Orientation, trigger: HTMLElement): void {
    this.returnFocus = trigger;
    this.viewing = orientation;
    this.changeDetector.detectChanges();
    const viewer = this.viewer?.nativeElement;
    if (viewer && typeof viewer.requestFullscreen === 'function') {
      viewer.requestFullscreen().catch(() => undefined);
    }
    this.viewerClose?.nativeElement.focus();
  }

  public closeViewer(): void {
    if (!this.viewing) {
      return;
    }
    if (this.document.fullscreenElement && this.document.fullscreenElement === this.viewer?.nativeElement) {
      this.document.exitFullscreen().catch(() => undefined);
    }
    this.viewing = null;
    // After the browser has left full screen, or the focus lands nowhere.
    const trigger = this.returnFocus;
    this.returnFocus = null;
    setTimeout(() => trigger?.focus({ preventScroll: true }));
  }

  // A click beside the frame, not on it.
  public onViewerClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeViewer();
    }
  }

  // Esc (or the browser's own control) left full screen: the viewer goes with it.
  @HostListener('document:fullscreenchange')
  public onFullscreenChange(): void {
    if (this.viewing && !this.document.fullscreenElement) {
      this.closeViewer();
    }
  }

  // The overlay has no browser full screen to leave: Esc closes it directly.
  @HostListener('document:keydown.escape')
  public onEscape(): void {
    this.closeViewer();
  }
}
